import type { IJobDefinition } from '@escloud/types';
import { useState } from 'react';

import type { SlotDescriptor } from './slot-card';

import type { WizardState } from './index';

import { useApi } from '@/context';
import { useCreateCase } from '@/hooks';
import { errorIds, logError } from '@/lib/log-error';
import { ApiError } from '@/services';

export interface SubmitParams {
    workflowId: string;
    initialJob: { key: string; definition: IJobDefinition } | null;
    slots: SlotDescriptor[];
    state: WizardState;
    /**
     * Invoked when the server returns 409 `dataset_unavailable` (sweeper
     * reaped a picked dataset between selection and atomic create). The
     * wizard clears the affected slot and bounces the user back to the
     * imaging step.
     */
    onDatasetUnavailable?: (info: DatasetUnavailable) => void;
}

export interface SubmitOutcome {
    caseId: string;
    /**
     * Present when the case was created successfully but the follow-up
     * initial-job POST failed. The case is already persisted (and
     * counted against quota), so the wizard navigates to the case page
     * rather than leaving the user staring at a generic error.
     */
    jobError?: { message: string };
}

/**
 * Surfaces a 409 `dataset_unavailable` response from the atomic case
 * create. The wizard uses this to clear the affected slot's pick and
 * bounce back to the imaging step instead of showing a generic error.
 */
export interface DatasetUnavailable {
    inputDatasetId: string;
    slotKey: string | null;
}

function readDatasetUnavailable(err: unknown): DatasetUnavailable | null {
    if (!(err instanceof ApiError) || err.status !== 409) return null;
    if (err.body?.error !== 'dataset_unavailable') return null;
    const details = err.body.details as { inputDatasetId?: string; slotKey?: string } | undefined;
    if (!details?.inputDatasetId) return null;
    return {
        inputDatasetId: details.inputDatasetId,
        slotKey: details.slotKey ?? null,
    };
}

export interface UseWizardSubmitResult {
    submit: (params: SubmitParams) => Promise<SubmitOutcome | null>;
    isSubmitting: boolean;
    currentStep: string;
    error: string | null;
}

/**
 * Wizard submit pipeline.
 *
 * Reconstructions upload eagerly at reconstruction time (see
 * `useEagerDicomUpload`), so by the time the user reaches the review
 * step every filled slot is bound to a server-side `InputDataset` id
 * in `state.slotPicks`. Submit collapses to:
 *
 *   1. `POST /cases { ..., inputs }` — atomic case create with all
 *      slot bindings in the same transaction (commit E-3).
 *   2. `POST /cases/:id/jobs` — kick off the workflow's first job
 *      (skipped for dataset-only workflows).
 *
 * No per-slot byte loop runs here any more; that simplification is
 * the whole point of the eager-upload refactor.
 */
export function useWizardSubmit(): UseWizardSubmitResult {
    const api = useApi();
    const createCase = useCreateCase();

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [currentStep, setCurrentStep] = useState('');
    const [error, setError] = useState<string | null>(null);

    const submit = async ({
        workflowId,
        initialJob,
        state,
        onDatasetUnavailable,
    }: SubmitParams): Promise<SubmitOutcome | null> => {
        setIsSubmitting(true);
        setError(null);

        let createdCaseId: string | null = null;
        try {
            setCurrentStep('Creating case…');
            const pickedInputs = Object.fromEntries(Object.entries(state.slotPicks).filter(([, id]) => id));
            const createdCase = await createCase.mutateAsync({
                workflowId,
                name: state.patientDetails.name,
                lastname: state.patientDetails.lastname || undefined,
                firstname: state.patientDetails.firstname || undefined,
                dob: state.patientDetails.dob || undefined,
                gender: state.patientDetails.gender || undefined,
                inputs: Object.keys(pickedInputs).length > 0 ? pickedInputs : undefined,
            });
            createdCaseId = createdCase.id;

            if (initialJob) {
                setCurrentStep('Starting processing…');
                try {
                    await api.jobs.create(createdCase.id, { jobKey: initialJob.key });
                } catch (jobErr) {
                    // The case row + slot bindings are committed; only
                    // the kickoff failed. Surface the partial success so
                    // the wizard can route the user to the case page
                    // with a retry affordance, instead of dropping them
                    // on a generic "Failed to create case" toast that
                    // hides a created-but-stalled case.
                    const message = jobErr instanceof Error ? jobErr.message : 'Failed to start processing';
                    logError(errorIds.WIZARD_INITIAL_JOB_FAILED, message, {
                        caseId: createdCase.id,
                        jobKey: initialJob.key,
                        error: jobErr,
                    });
                    return { caseId: createdCase.id, jobError: { message } };
                }
            }

            return { caseId: createdCase.id };
        } catch (err) {
            // Sweeper-reaped dataset between pick and atomic create: tell
            // the parent which slot to clear so the wizard can bounce
            // back to imaging with a friendly toast instead of leaving a
            // stale id in `state.slotPicks` that would fail again on
            // retry.
            const unavailable = readDatasetUnavailable(err);
            if (unavailable) {
                onDatasetUnavailable?.(unavailable);
                setError(
                    unavailable.slotKey
                        ? `Dataset for "${unavailable.slotKey}" was removed before the case could be created. Re-select it and try again.`
                        : 'A referenced dataset was removed before the case could be created. Re-select it and try again.'
                );
                return null;
            }

            const message = err instanceof Error ? err.message : 'Failed to create case';
            logError(errorIds.WIZARD_SUBMIT_FAILED, message, {
                workflowId,
                createdCaseId,
                error: err,
            });
            setError(message);
            // Defensive: if we reached this catch *after* the case row
            // was created (createCase resolved, then a synchronous error
            // bubbled later), still surface the partial success.
            if (createdCaseId) {
                return { caseId: createdCaseId, jobError: { message } };
            }
            return null;
        } finally {
            setIsSubmitting(false);
            setCurrentStep('');
        }
    };

    return { submit, isSubmitting, currentStep, error };
}
